import { Link } from "@inertiajs/react"
import { ArrowDown, ArrowRight, ArrowUpRight, Plus } from "lucide-react"
import { Button } from "@/components/ui/button"
import { MarketingLayout } from "@/components/marketing/MarketingLayout"
import { MarketingMetadata } from "@/components/marketing/MarketingMetadata"
import { HOME_COPY } from "@/content/marketing-home"
import type { MarketingPageProps } from "@/types/marketing"
import founderImage from "../../assets/marketing/antonio-founder.webp"
import teaImage from "../../assets/marketing/projects/cajanka-guayusa.webp"
import webshopImage from "../../assets/marketing/projects/cajanka-webshop.webp"
import vanCleefImage from "../../assets/marketing/clients/vancleef/login.webp"

export default function MarketingHome(props: MarketingPageProps) {
  const copy = HOME_COPY[props.locale]
  const hr = props.locale === "hr"
  const structuredData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        name: "Zmile d.o.o.",
        url: props.alternate_urls.hr,
        email: "info@zmile.hr",
        description: props.description,
        founder: {
          "@type": "Person",
          name: "Antonio Novoselnik",
          jobTitle: hr ? "Osnivač" : "Founder",
        },
      },
      {
        "@type": "FAQPage",
        mainEntity: copy.faq.items.map((item) => ({
          "@type": "Question",
          name: item.question,
          acceptedAnswer: { "@type": "Answer", text: item.answer },
        })),
      },
    ],
  }
  const projects = [
    {
      key: "cajanka",
      name: "Čajanka",
      href: props.cajanka_href,
      image: webshopImage,
      alt: hr
        ? "Stvarna Čajanka web-trgovina, snimljena u rujnu 2026."
        : "The actual Čajanka webshop, captured in September 2026.",
      ...copy.proof.cajanka,
    },
    {
      key: "vancleef",
      name: "VanCleef",
      href: props.vancleef_href,
      image: vanCleefImage,
      alt: hr
        ? "Prijava u stvarnu VanCleef operativnu platformu"
        : "Login to the real VanCleef operations platform",
      ...copy.proof.vancleef,
    },
  ]

  return (
    <>
      <MarketingMetadata
        alternateUrls={props.alternate_urls}
        canonicalUrl={props.canonical_url}
        description={props.description}
        locale={props.locale}
        noindex={props.noindex}
        socialImageUrl={props.social_image_url}
        structuredData={structuredData}
        title={props.title}
      />
      <MarketingLayout
        consultationHref={props.consultation_href}
        currentPageKey={props.page_key}
        footer={props.footer}
        homeHref={props.home_href}
        languageSwitch={props.language_switch}
        locale={props.locale}
        navigation={props.navigation}
      >
        <section className="studio-hero">
          <div className="studio-hero-copy">
            <h1>
              {hr ? (
                <>
                  Bolja web-stranica <br />
                  <span>samo je početak.</span>
                </>
              ) : (
                <>
                  A better website <br />
                  <span>is only the beginning.</span>
                </>
              )}
            </h1>
            <p className="studio-hero-summary">
              {hr
                ? "Gradimo web-stranice i povezujemo poslovne sustave. Da vas kupci lakše razumiju, a vaš tim jednostavnije radi."
                : "We build websites and connect business systems. So customers understand your value, and your team can get on with the work."}
            </p>
            <div className="studio-actions">
              <Button asChild variant="marketing" size="marketingLg">
                <Link
                  href={props.consultation_href}
                  data-analytics-id="home-hero-consultation"
                  aria-label={copy.hero.action}
                >
                  {hr
                    ? "Razgovarajmo o vašem poslu"
                    : "Let's talk about your business"}
                  <ArrowUpRight aria-hidden="true" />
                </Link>
              </Button>
              <Link className="studio-text-link" href={props.work_href}>
                {hr ? "Pogledajte radove" : "Explore our work"}
                <ArrowRight aria-hidden="true" />
              </Link>
            </div>
            <small>{copy.hero.microcopy}</small>
          </div>
          <Link
            href={props.cajanka_href}
            className="studio-project-cover"
            aria-label={copy.proof.cajanka.link}
          >
            <div className="studio-project-art">
              <img
                className="studio-tea-texture"
                src={teaImage}
                alt=""
                width="768"
                height="1024"
                fetchPriority="high"
              />
              <div className="studio-cover-title" aria-hidden="true">
                Čajanka
                <span>
                  {hr ? "Web i poslovni sustavi" : "Web & operations"}
                </span>
              </div>
              <img
                className="studio-webshop-preview"
                src={webshopImage}
                alt={
                  hr
                    ? "Prikaz Čajanka web-trgovine"
                    : "A view of the Čajanka webshop"
                }
                width="1440"
                height="1000"
              />
            </div>
            <div className="studio-project-caption">
              <span>
                {hr
                  ? "Jedna trgovina. Povezano poslovanje."
                  : "One shop. Connected operations."}
                <small>
                  {hr
                    ? "Čajanka · u vlasništvu Zmile d.o.o."
                    : "Čajanka · owned by Zmile d.o.o."}
                </small>
              </span>
              <ArrowUpRight aria-hidden="true" />
            </div>
          </Link>
          <div className="studio-hero-bottom">
            <span>{copy.hero.eyebrow}</span>
            <a href="#selected-work">
              {hr ? "Stvarni projekti, u nastavku" : "Real projects, below"}
              <ArrowDown aria-hidden="true" />
            </a>
          </div>
        </section>

        <section className="studio-work" id="selected-work">
          <header className="studio-section-heading">
            <h2>
              {hr ? (
                <>
                  Dobar rad.
                  <br />U stvarnoj uporabi.
                </>
              ) : (
                <>
                  Good work.
                  <br />
                  Out in the real world.
                </>
              )}
            </h2>
            <p>
              {hr
                ? "Od web-trgovine do računovodstva. Pogledajte što smo izgradili, kako radi i što možemo potkrijepiti."
                : "From a webshop to accounting. See what we built, how it works, and the evidence behind it."}
            </p>
          </header>
          <div className="studio-work-grid">
            {projects.map((project) => (
              <article
                key={project.key}
                className={`studio-case studio-case-${project.key}`}
              >
                <Link
                  className="studio-case-image"
                  href={project.href}
                  aria-label={project.link}
                >
                  <img
                    src={project.image}
                    alt={project.alt}
                    width={project.key === "cajanka" ? 1440 : 1280}
                    height={project.key === "cajanka" ? 1000 : 720}
                    loading="lazy"
                  />
                  <span>
                    <ArrowUpRight aria-hidden="true" />
                  </span>
                </Link>
                <h3>{project.heading}</h3>
                <p className="studio-relationship">{project.relationship}</p>
                <p className="studio-case-description">{project.built}</p>
                <details className="studio-case-evidence">
                  <summary>
                    {hr ? "Problem i dokazi" : "Problem and evidence"}
                    <Plus aria-hidden="true" />
                  </summary>
                  <p>{project.problem}</p>
                  <ul>
                    {project.metrics.map((metric) => (
                      <li key={metric}>{metric}</li>
                    ))}
                  </ul>
                </details>
                <Link
                  href={project.href}
                  data-analytics-id={`home-${project.key}`}
                  className="studio-text-link"
                >
                  {project.link}
                  <ArrowRight aria-hidden="true" />
                </Link>
              </article>
            ))}
          </div>
        </section>

        <section className="studio-services">
          <header className="studio-section-heading">
            <h2>
              {hr ? (
                <>
                  Vaš posao,
                  <br />s manje prepreka.
                </>
              ) : (
                <>
                  Your business.
                  <br />
                  Fewer obstacles.
                </>
              )}
            </h2>
            <div>
              <p>{copy.builds.introduction}</p>
              <Link className="studio-text-link" href={props.services_href}>
                {copy.builds.link}
                <ArrowUpRight aria-hidden="true" />
              </Link>
            </div>
          </header>
          <div className="studio-service-list">
            {copy.builds.items.map((item, index) => (
              <article key={item.title}>
                <h3>{item.title}</h3>
                <div>
                  <p>{item.body}</p>
                  <p className="studio-service-outcome">{item.artifact}</p>
                </div>
                <ArrowUpRight aria-hidden="true" />
                <p className="studio-service-problem">
                  {copy.audience.problems[index]}
                </p>
              </article>
            ))}
          </div>
          <p className="studio-ai-note">{copy.builds.principle}</p>
        </section>

        <section className="studio-business">
          <div>
            <h2>{copy.experiences.heading}</h2>
            <p>{copy.experiences.body}</p>
            <p className="studio-audience-intro">{copy.audience.heading}</p>
            <ul className="studio-audience-roles">
              {copy.audience.roles.map((role) => (
                <li key={role}>{role}</li>
              ))}
            </ul>
          </div>
          <div className="studio-business-sides">
            {[
              {
                title: copy.experiences.visibleLabel,
                body: copy.experiences.visibleTitle,
                items: copy.experiences.visibleItems,
              },
              {
                title: copy.experiences.operatingLabel,
                body: copy.experiences.operatingTitle,
                items: copy.experiences.operatingItems,
              },
            ].map((side) => (
              <article key={side.title}>
                <h3>{side.title}</h3>
                <p>{side.body}</p>
                <ul>
                  {side.items.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </article>
            ))}
          </div>
        </section>

        <section className="studio-process" id="process">
          <header className="studio-section-heading">
            <h2>{copy.process.heading}</h2>
            <div>
              <p>{copy.process.prepare}</p>
              <p>{copy.process.after}</p>
            </div>
          </header>
          <ol>
            {copy.process.items.map((item, index) => (
              <li key={item.title}>
                <span>{index + 1}</span>
                <h3>{item.title}</h3>
                <p>{item.body}</p>
              </li>
            ))}
          </ol>
        </section>

        <section className="studio-founder">
          <figure>
            <img
              alt={copy.trust.imageAlt}
              src={founderImage}
              width="1200"
              height="1500"
              loading="lazy"
            />
            <figcaption>
              <strong>{copy.trust.name}</strong>
              <span>{copy.trust.role}</span>
            </figcaption>
          </figure>
          <div>
            <h2>{copy.trust.heading}</h2>
            <p>{copy.trust.body}</p>
            <Link className="studio-text-link" href={props.about_href}>
              {copy.trust.link}
              <ArrowUpRight aria-hidden="true" />
            </Link>
          </div>
        </section>

        <section className="studio-faq">
          <h2>{copy.faq.heading}</h2>
          <div>
            {copy.faq.items.map((item, index) => (
              <details
                key={item.question}
                data-analytics-id={`home-faq-${index + 1}`}
              >
                <summary>
                  {item.question}
                  <Plus aria-hidden="true" />
                </summary>
                <p>{item.answer}</p>
              </details>
            ))}
          </div>
        </section>

        <section className="studio-final">
          <h2>{copy.final.heading}</h2>
          <div>
            <p>{copy.final.body}</p>
            <Button asChild variant="marketing" size="marketingLg">
              <Link
                href={props.consultation_href}
                aria-label={copy.final.action}
                data-analytics-id="home-final-consultation"
              >
                {hr ? "Dogovorimo razgovor" : "Let's arrange a call"}
                <ArrowUpRight aria-hidden="true" />
              </Link>
            </Button>
            <small>{copy.final.microcopy}</small>
          </div>
        </section>
      </MarketingLayout>
    </>
  )
}
